import * as Sentry from '@sentry/react';

import { isDev } from '~/dev/utils/isDev';

export const captureException = (
  message: string,
  error: any,
  tags: Record<string, string>,
) => {
  if (!isDev) {
    Sentry.captureException(error, {
      tags: tags,
      extra: { message },
    });
  }
};

export const captureMessage = (
  message: string,
  tags: Record<string, string>,
) => {
  if (!isDev) {
    Sentry.captureMessage(message, { tags });
  }
};

export type ForcedLogoutReason =
  | 'token_refresh_failed'
  | 'rolodex_refresh_failed'
  | 'client_subdomain_mismatch'
  | 'cross_client_switch'
  | 'inactivity_timeout'
  | 'sign_in_required_modal';

/** Counts a forced logout in Sentry metrics, split by reason. */
export function countForcedLogout(reason: ForcedLogoutReason) {
  if (!isDev) {
    Sentry.metrics.count('auth.forced_logout', 1, { attributes: { reason } });
  }
}

const SENTRY_TAG_VALUE_MAX_LENGTH = 200;

function toSentryTagValue(value: unknown) {
  if (typeof value !== 'object' || value === null) return String(value);

  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

/** Coerces an arbitrary details object into Sentry's string-only tag shape. */
export function toSentryTags(
  details: Record<string, unknown>,
): Record<string, string> {
  return Object.fromEntries(
    Object.entries(details).map(([key, value]) => [
      key,
      toSentryTagValue(value).slice(0, SENTRY_TAG_VALUE_MAX_LENGTH),
    ]),
  );
}
